import React, {
    Component,
    ComponentClass,
    Context,
    ContextType,
    createElement,
    FC,
    memo,
    PropsWithChildren,
    ReactNode
} from "react";
import { hydrate, render, unmountComponentAtNode } from "react-dom";
import { object } from "prop-types";
import { ReactRouterContextProvider } from "./ReactRouterContext";
import { wrappedDisplayName } from "../IntlRootMixin";

interface BridgeProps {
    router: Record<PropertyKey, unknown>;
}

const Bridge: (Component: any) => FC<PropsWithChildren<BridgeProps>> = (Component) => {
    const Bridged = memo(({ router, ...rest }: BridgeProps) => {
        return (
            <ReactRouterContextProvider router={router}>
                <Component {...rest} />
            </ReactRouterContextProvider>
        );
    });

    Bridged.displayName = wrappedDisplayName("ReactRouterBridge", Component);
    return Bridged;
};

export type ReactAdapterProps<P> = {
    component: FC<P> | ComponentClass<P> | string;
    children?: ReactNode;
} & Record<PropertyKey, unknown>;

export class ReactAdapter<P> extends Component<ReactAdapterProps<P>> {
    static readonly contextTypes = { router: object.isRequired };
    context!: ContextType<Context<{ router: Record<PropertyKey, unknown> }>>;

    private readonly Bridged: FC<BridgeProps> | string = "";
    private refHold: HTMLDivElement | null = null;

    constructor(props: any) {
        super(props);
        this.Bridged = Bridge(props.component);
    }

    init = (shouldHydrate?: boolean) => {
        (async () => {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { component, children, ...rest } = this.props;
            const renderMethod = shouldHydrate ? hydrate : render;
            renderMethod(createElement(this.Bridged, { router: this.context.router, ...rest }, children), this.refHold);
        })();
    };

    componentDidUpdate() {
        this.init(true);
    }

    componentDidMount() {
        this.init();
    }

    componentWillUnmount() {
        unmountComponentAtNode(this.refHold as HTMLDivElement);
        this.refHold = null;
    }

    render() {
        return <div ref={(ref) => (this.refHold = ref)} />;
    }
}
